import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { CheckCircle2, Lock, Printer, Loader2 } from "lucide-react";

/**
 * StrategyShareView — the client-facing rendered HTML view of a strategy.
 * Reached via `/strategy/share/:token`. Print-optimised CSS mirrors the
 * sample GBE strategy document (cover page, sections, callouts, tables).
 * Auto-triggers window.print() when `?print=1` query is present, which
 * is how the PDF export path (Playwright) captures a print-quality PDF.
 */
export default function StrategyShareView() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [signedRole, setSignedRole] = useState("");

  useEffect(() => {
    api.get(`/strategies/share/${token}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [token]);

  // If the strategy has a completed white_paper, render that HTML in
  // full-page mode instead of the manual sections. Toggle via `?wp=1`
  // (default when wp is complete) or force fallback via `?wp=0`.
  const wpMode = (() => {
    const explicit = params.get("wp");
    if (explicit === "0") return false;
    if (explicit === "1") return true;
    return doc?.white_paper?.status === "complete"
      && !!doc?.white_paper?.phase_6_html;
  })();

  useEffect(() => {
    if (doc && params.get("print") === "1") {
      setTimeout(() => window.print(), 800);
    }
  }, [doc, params]);

  const approve = async () => {
    if (!signedName.trim()) { toast.error("Enter your name"); return; }
    setSigning(true);
    try {
      await api.post(`/strategies/share/${token}/approve`, {
        signed_name: signedName.trim(),
        signed_role: signedRole.trim(),
      });
      toast.success("Approved & locked");
      const { data } = await api.get(`/strategies/share/${token}`);
      setDoc(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setSigning(false); }
  };

  if (err) {
    return <div className="p-12 text-center">
      <h1 className="text-2xl font-display font-bold">Strategy unavailable</h1>
      <p className="text-sm text-zinc-500 mt-2">{err}</p>
    </div>;
  }
  if (!doc) {
    return <div className="p-12 text-center text-zinc-500">
      <Loader2 className="h-6 w-6 animate-spin inline" /> Loading…
    </div>;
  }

  const locked = doc.status === "locked";
  const clientReview = doc.status === "client_review";

  // ── White-paper mode ─────────────────────────────────────────
  // The Phase-6 output is a complete standalone HTML document. When
  // present + wpMode toggled on, we render it directly (skipping the
  // manual-sections view) so the print/PDF pipeline captures exactly
  // the print-ready white paper — nothing else.
  if (wpMode && doc?.white_paper?.phase_6_html) {
    return (
      <WhitePaperRenderer html={doc.white_paper.phase_6_html}
        onPrint={() => window.print()} />
    );
  }

  // Extract live-data snapshot totals for surface-level display
  const liveSources = doc.live_data_snapshot?.live_sources || [];

  return (
    <div className="strategy-share">
      <style>{`
        @page { size: A4; margin: 20mm; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-after: always; }
        }
        .strategy-share {
          font-family: Georgia, 'Times New Roman', serif;
          color: #1a1a1a; background: #f5f5f0;
          min-height: 100vh; padding: 40px 20px;
        }
        .strategy-share .doc {
          max-width: 900px; margin: 0 auto; background: white;
          padding: 60px 70px; box-shadow: 0 4px 24px rgba(0,0,0,0.08);
        }
        .strategy-share h1.title {
          font-family: Georgia, serif; font-size: 32pt;
          font-weight: 700; margin: 0 0 10px 0; letter-spacing: -0.5px;
          color: #002FA7;
        }
        .strategy-share .subtitle {
          font-size: 12pt; color: #666; margin-bottom: 40px;
          border-bottom: 2px solid #002FA7; padding-bottom: 12px;
        }
        .strategy-share .cover-meta {
          margin: 20px 0 40px; font-size: 10pt; color: #444;
        }
        .strategy-share .cover-meta td:first-child {
          font-weight: bold; padding-right: 20px; padding-bottom: 4px;
        }
        .strategy-share h2 {
          font-size: 18pt; font-weight: 700; margin: 40px 0 12px;
          color: #002FA7; border-bottom: 1px solid #ddd; padding-bottom: 6px;
        }
        .strategy-share h3 {
          font-size: 13pt; font-weight: 700; margin: 24px 0 8px; color: #333;
        }
        .strategy-share p { font-size: 11pt; line-height: 1.6; margin: 0 0 12px; }
        .strategy-share .callout {
          background: #fff8e1; border-left: 4px solid #f59e0b;
          padding: 14px 18px; margin: 16px 0; font-size: 10.5pt;
        }
        .strategy-share .stat-row {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px; margin: 16px 0;
        }
        .strategy-share .stat-box {
          border: 1px solid #ddd; padding: 10px 12px; background: #fafafa;
        }
        .strategy-share .stat-box .num {
          font-size: 20pt; font-weight: 700; color: #002FA7; line-height: 1;
        }
        .strategy-share .stat-box .lbl {
          font-size: 9pt; color: #666; text-transform: uppercase;
          letter-spacing: 0.5px; margin-top: 4px;
        }
        .strategy-share table.data {
          width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 10pt;
        }
        .strategy-share table.data th, .strategy-share table.data td {
          border: 1px solid #ccc; padding: 6px 10px; text-align: left;
        }
        .strategy-share table.data th {
          background: #eef2ff; color: #002FA7; font-weight: 700; font-size: 9pt;
        }
        .strategy-share .status-badge {
          display: inline-block; padding: 4px 10px; border-radius: 3px;
          font-size: 9pt; font-weight: bold; letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .strategy-share .approval-panel {
          margin-top: 60px; padding: 30px; border: 2px solid #002FA7;
          background: #f5f8ff;
        }
        .strategy-share .signed-block {
          margin-top: 40px; padding: 24px; border: 2px solid #7c3aed;
          background: #f5f3ff;
        }
      `}</style>

      <div className="doc">
        {/* Cover */}
        <div>
          <div className="status-badge" style={{
            background: locked ? "#f5f3ff" : clientReview ? "#fff8e1" : "#eef2ff",
            color: locked ? "#7c3aed" : clientReview ? "#92400e" : "#002FA7",
            border: `1px solid ${locked ? "#7c3aed" : clientReview ? "#f59e0b" : "#002FA7"}`,
          }}>
            {locked ? "SIGNED & LOCKED" : clientReview ? "AWAITING YOUR APPROVAL" : doc.status.toUpperCase()}
          </div>
          <h1 className="title" data-testid="strategy-view-title">{doc.title}</h1>
          <div className="subtitle">
            {doc.period_type[0].toUpperCase() + doc.period_type.slice(1)} strategy
            {" · "}covering {doc.prev_period_start} → {doc.prev_period_end}
          </div>
          <table className="cover-meta">
            <tbody>
              <tr><td>Prepared for</td><td>{doc.cover_meta?.prepared_for}</td></tr>
              <tr><td>Prepared by</td><td>{doc.cover_meta?.prepared_by}</td></tr>
              <tr><td>Date</td><td>{new Date(doc.cover_meta?.date || doc.updated_at).toLocaleDateString()}</td></tr>
              <tr><td>Data sources</td><td>{doc.cover_meta?.data_sources}</td></tr>
            </tbody>
          </table>

          {doc.objectives && (
            <div className="callout">
              <strong>Client objectives:</strong> {doc.objectives}
            </div>
          )}
        </div>

        <div className="page-break" />

        {/* Live-data snapshot (auto-populated from previous period) */}
        {liveSources.length > 0 && (
          <div>
            <h2>Baseline metrics (previous {doc.period_type})</h2>
            <p>
              Live snapshot from {doc.live_data_snapshot?.period_start} to{" "}
              {doc.live_data_snapshot?.period_end}, captured{" "}
              {new Date(doc.live_data_snapshot?.generated_at).toLocaleString()}.
            </p>
            {liveSources.filter((s) => s.status === "ok").map((s, i) => (
              <div key={`${s.platform}-${s.business_unit_id}-${i}`}
                data-testid={`live-source-view-${s.platform}-${i}`}>
                <h3>
                  {(s.label || s.platform)} —{" "}
                  {s.business_unit_name || s.resource_label || "Overall"}
                </h3>
                <div className="stat-row">
                  {Object.entries(s.data?.totals || {}).slice(0, 6).map(([k, v]) => (
                    <div key={k} className="stat-box">
                      <div className="num">
                        {typeof v === "number" ? v.toLocaleString(undefined,
                          { maximumFractionDigits: 2 }) : String(v)}
                      </div>
                      <div className="lbl">{k.replace(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="page-break" />

        {/* Editor-authored sections */}
        {(doc.sections || []).map((s, i) => (
          <div key={s.id}
            data-testid={`strategy-view-section-${i}`}>
            <h2>{s.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.content_html || "") }} />
          </div>
        ))}

        {/* Approval / signed block */}
        {locked ? (
          <div className="signed-block">
            <h3 style={{ marginTop: 0, color: "#7c3aed" }}>
              <Lock className="inline" style={{ height: "16px", width: "16px" }} />
              {" "}Signed & locked
            </h3>
            <p>
              Approved by <strong>{doc.signed_name}</strong>
              {doc.signed_role && <> ({doc.signed_role})</>} on{" "}
              {new Date(doc.signed_at).toLocaleString()}
            </p>
            <p style={{ fontSize: "9pt", color: "#666" }}>
              This strategy is now locked. Any changes require a new revision.
            </p>
          </div>
        ) : clientReview ? (
          <div className="approval-panel no-print" data-testid="strategy-approval-panel">
            <h3 style={{ marginTop: 0, color: "#002FA7" }}>Approve this strategy</h3>
            <p>
              By approving, you accept this strategy for the upcoming{" "}
              {doc.period_type}. It will be locked from further changes;
              a new revision is required for any updates.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "16px" }}>
              <div>
                <label style={{ fontSize: "9pt", fontWeight: "bold", color: "#333" }}>
                  Your full name *
                </label>
                <Input value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="e.g. Darren Andrews"
                  data-testid="strategy-sign-name"
                  style={{ marginTop: "4px" }} />
              </div>
              <div>
                <label style={{ fontSize: "9pt", fontWeight: "bold", color: "#333" }}>
                  Your role (optional)
                </label>
                <Input value={signedRole}
                  onChange={(e) => setSignedRole(e.target.value)}
                  placeholder="e.g. Managing Director"
                  data-testid="strategy-sign-role"
                  style={{ marginTop: "4px" }} />
              </div>
            </div>
            <Button onClick={approve} disabled={signing || !signedName.trim()}
              style={{ marginTop: "20px", background: "#002FA7", color: "white" }}
              data-testid="strategy-approve-btn">
              {signing
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</>
                : <><CheckCircle2 className="h-4 w-4 mr-2" /> Approve & lock strategy</>}
            </Button>
          </div>
        ) : null}

        <div className="no-print" style={{ marginTop: "40px", textAlign: "center" }}>
          <Button variant="outline" onClick={() => window.print()}
            data-testid="strategy-view-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / save as PDF
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * WhitePaperRenderer — renders the Phase-6 assembled HTML document
 * exactly as Claude produced it. The Phase-6 prompt specifies a fully
 * self-contained document (own DOCTYPE, own <style> block, no external
 * assets), so we drop it into a same-origin iframe using srcDoc. That
 * gives us print-ready parity — Playwright PDF-captures the iframe's
 * printed output identical to what the client sees on-screen.
 *
 * The srcDoc approach also isolates the white paper's CSS from the
 * host app's Tailwind / Shadcn styles, preventing any accidental
 * bleed of app chrome into the printed PDF.
 */
function WhitePaperRenderer({ html }) {
  // Auto-trigger the iframe's own print dialog when `?print=1` is set —
  // Playwright uses this path to capture the PDF via
  // `page.pdf({ format: "A4" })` after `window.print()` finalises
  // any print-only CSS.
  const iframeRef = React.useRef(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("print") === "1") {
      const t = setTimeout(() => {
        try {
          const win = iframeRef.current?.contentWindow;
          if (win) { win.focus(); win.print(); }
          else { window.print(); }
        } catch (_) { window.print(); }
      }, 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);
  return (
    <div className="whitepaper-shell">
      <style>{`
        @media print {
          body, .whitepaper-shell { margin: 0 !important; padding: 0 !important; background: white !important; }
          .whitepaper-shell iframe { border: 0 !important; height: 100vh !important; }
          .no-print { display: none !important; }
        }
        .whitepaper-shell {
          background: #eee; min-height: 100vh; padding: 0;
        }
        .whitepaper-shell iframe {
          width: 100%; height: 100vh; border: 0; display: block;
          background: white;
        }
        .whitepaper-shell .wp-toolbar {
          position: fixed; top: 12px; right: 12px; z-index: 10;
          background: white; padding: 6px 10px; border-radius: 4px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          display: flex; gap: 6px; align-items: center;
          font-size: 12px; font-family: system-ui, sans-serif;
        }
      `}</style>
      <div className="wp-toolbar no-print">
        <span style={{ color: "#666" }}>SEO White Paper</span>
        <Button size="sm" variant="outline" onClick={() => {
          try { iframeRef.current?.contentWindow?.print(); }
          catch (_) { window.print(); }
        }} data-testid="whitepaper-print-btn">
          <Printer className="h-3.5 w-3.5 mr-1" /> Print / PDF
        </Button>
      </div>
      <iframe ref={iframeRef} title="White Paper"
        srcDoc={html} data-testid="whitepaper-iframe" />
    </div>
  );
}
